import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import {
    FacebookShareButton,
    TwitterShareButton,
    WhatsappShareButton,
    PinterestShareButton,
    TelegramShareButton,
    FacebookIcon,
    TwitterIcon,
    WhatsappIcon,
    PinterestIcon,
    TelegramIcon
} from 'react-share';
import { LanguageContext } from '../App';

function History() {
    const [looks, setLooks] = useState(null);
    const [error, setError] = useState(null);
    const { language } = useContext(LanguageContext);
    const baseUrl = window.location.origin;

    const getEmailFromCookies = () => {
        const email = document.cookie.replace(/(?:(?:^|.*;\s*)user_email\s*\=\s*([^;]*).*$)|^.*$/, "$1");
        console.log('Email from cookies:', email);
        return email;
    };

    // Translations for the component
    const texts = {
        en: {
            history: 'History',
            loading: 'Loading history...',
            noHistory: 'No history found.',
            shareTitle: 'Check out my outfit from Style Forecast!',
            connectionError: 'Cannot connect to server. Please check your internet connection and try again.',
            loadingError: 'Error loading history: '
        },
        sr: {
            history: 'Istorija',
            loading: 'Učitavanje istorije...',
            noHistory: 'Nema pronađene istorije.',
            shareTitle: 'Pogledajte moj outfit sa Style Forecast!',
            connectionError: 'Nije moguće povezati se sa serverom. Proverite internet vezu i pokušajte ponovo.',
            loadingError: 'Greška pri učitavanju istorije: '
        }
    };

    // Get the correct texts based on language
    const t = texts[language] || texts.en;

    useEffect(() => {
        const fetchLooks = async () => {
            const email = getEmailFromCookies();
            console.log('Email from cookies in History.js:', email);
            if (!email) {
                console.error('User email is not available for fetching history.');
                setError('User email is not available');
                setLooks([]);
                return;
            }
            try {
                const response = await axios.get(`/getUserLooks`, {
                    params: { email }
                });
                console.log('User looks fetched:', response.data);
                if (Array.isArray(response.data)) {
                    // Sort looks by date so the most recent appears first
                    const sortedLooks = response.data.sort((a, b) => new Date(b.look_date) - new Date(a.look_date));
                    setLooks(sortedLooks);
                } else {
                    console.error('Error: Looks data is not an array');
                    setLooks([]);
                    setError('Invalid data format received');
                }
            } catch (error) {
                console.error('Error fetching looks:', error);
                
                // Check for connection errors
                if (error.code === 'ERR_NETWORK') {
                    setLooks([]);
                    setError(t.connectionError);
                } else {
                    setLooks([]);
                    setError(t.loadingError + (error.response?.data || error.message));
                }
            }
        };
        fetchLooks();
    }, [t.connectionError, t.loadingError]);        

    if (looks === null) {
        return <div>{t.loading}</div>;
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    return (
        <div className="history-container">
            <h2>{t.history}</h2>
            <div className="history-content">
                {looks.length > 0 ? (
                    looks.map((look, index) => {
                        let products = [];
                        let productLinks = {};
                        
                        try {
                            // Parse products safely
                            if (typeof look.look_products === 'string') {
                                products = JSON.parse(look.look_products);
                            } else if (Array.isArray(look.look_products)) {
                                products = look.look_products;
                            }
                        } catch (e) {
                            console.error(`Error parsing look_products for look ${index}:`, e);
                            products = [];
                        }
                        
                        try {
                            // Parse product links safely
                            if (typeof look.products_links === 'string') {
                                productLinks = JSON.parse(look.products_links);
                            } else if (look.products_links && typeof look.products_links === 'object') {
                                productLinks = look.products_links;
                            }
                        } catch (e) {
                            console.error(`Error parsing products_links for look ${index}:`, e);
                            productLinks = {};
                        }
                        
                        // Ensure products is an array
                        if (!Array.isArray(products)) {
                            products = [];
                        }

                        // Create full image URL for sharing
                        const imageUrl = look.look_img.startsWith('http') 
                            ? look.look_img 
                            : `${baseUrl}${look.look_img}`;
                            
                        // Create share description
                        const description = look.look_description || t.shareTitle;
                        const shareDate = new Date(look.look_date).toLocaleDateString();

                        return (
                            <div key={index} className="history-card">
                                <p className="history-date">{shareDate}</p>
                                <img src={look.look_img} alt="Outfit" className="history-image" />
                                <p className="history-description">{look.look_description}</p>
                                <div className="share-buttons">
                                    <FacebookShareButton url={imageUrl} quote={description}>
                                        <FacebookIcon size={32} round />
                                    </FacebookShareButton>
                                    <TwitterShareButton url={imageUrl} title={description}>
                                        <TwitterIcon size={32} round />
                                    </TwitterShareButton>
                                    <WhatsappShareButton url={imageUrl} title={description}>
                                        <WhatsappIcon size={32} round />
                                    </WhatsappShareButton>
                                    <PinterestShareButton url={imageUrl} media={imageUrl} description={description}>
                                        <PinterestIcon size={32} round />
                                    </PinterestShareButton>
                                    <TelegramShareButton url={imageUrl} title={description}>
                                        <TelegramIcon size={32} round />
                                    </TelegramShareButton>
                                </div>
                                <div className="history-buttons">
                                    {products.map((productName, pIndex) => (
                                        <button 
                                            key={pIndex} 
                                            className="history-button" 
                                            onClick={() => {
                                                const link = productLinks[productName];
                                                if (link) window.open(link, '_blank');
                                            }}
                                        >
                                            {productName}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <p>{t.noHistory}</p>
                )}
            </div>
        </div>
    );
}

export default History;